// ** Modules
import React from 'react';
import { signOut } from 'next-auth/react';
import { useRouter } from 'next/router';

// ** UI
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

// ** Icons
import { Icon } from '@/components/common/icons';

// ** Components
import NextAvatar from '@/components/common/NextAvatar';

// ** Types
import type { User } from 'next-auth';

// Styled Components
const BadgeContentSpan = styled('span')(({ theme }) => ({
	width: 8,
	height: 8,
	borderRadius: '50%',
	backgroundColor: theme.palette.success.main,
	boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
}));

const UserDropdown = ({ currentUser }: { currentUser: User }) => {
	// States
	const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);

	const router = useRouter();

	const handleDropdownOpen = (event: React.SyntheticEvent) => {
		setAnchorEl(event.currentTarget);
	};

	const handleDropdownClose = (url?: string) => {
		if (url) {
			router.push(url);
		}
		setAnchorEl(null);
	};

	const styles = {
		py: 2,
		px: 4,
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		color: 'text.primary',
		textDecoration: 'none',
		'& svg': {
			mr: 2,
			fontSize: '1.375rem',
			color: 'text.primary'
		}
	};

	const handleLogout = () => {
		signOut({ callbackUrl: '/' });
		handleDropdownClose();
	};

	return (
		<React.Fragment>
			<Badge
				overlap="circular"
				onClick={handleDropdownOpen} // onClick applied here on Badge
				sx={{ ml: 2, cursor: 'pointer' }}
				badgeContent={<BadgeContentSpan />}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right'
				}}
			>
				<NextAvatar
					alt={currentUser?.name as string}
					src={undefined} // TODO: Add user avatar source
				/>
			</Badge>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={() => handleDropdownClose()}
				sx={{ '& .MuiMenu-paper': { width: 230, mt: 4 } }}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right'
				}}
			>
				<Box sx={{ pt: 2, pb: 3, px: 4 }}>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Badge
							overlap="circular"
							badgeContent={<BadgeContentSpan />}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'right'
							}}
						>
							<NextAvatar
								alt={currentUser?.name as string}
								src={undefined} // TODO: Add user avatar source
							/>
						</Badge>

						<Box
							sx={{
								display: 'flex',
								ml: 3,
								alignItems: 'flex-start',
								flexDirection: 'column'
							}}
						>
							<Typography sx={{ fontWeight: 600 }}>{currentUser?.name}</Typography>

							{/* <Typography variant="body-md" sx={{ fontSize: '0.8rem' }}>
								{currentUser?.person?.name?.fullName || currentUser?.name}
							</Typography> */}
						</Box>
					</Box>
				</Box>

				<Divider sx={{ mt: '0 !important' }} />

				<MenuItem sx={{ p: 0 }} onClick={() => handleDropdownClose('/user/profile')}>
					<Box sx={styles}>
						<Icon name="mdi:account-outline" />
						Profile
					</Box>
				</MenuItem>

				{/* <MenuItem
					sx={{ p: 0 }}
					onClick={() => handleDropdownClose('/apps/email')}
				>
					<Box sx={styles}>
						<Icon icon="mdi:email-outline" />
						Inbox
					</Box>
				</MenuItem>

				<MenuItem
					sx={{ p: 0 }}
					onClick={() => handleDropdownClose('/apps/chat')}
				>
					<Box sx={styles}>
						<Icon icon="mdi:message-outline" />
						Chat
					</Box>
				</MenuItem> */}

				{/* <Divider />

				<MenuItem
					sx={{ p: 0 }}
					onClick={() => handleDropdownClose('/pages/account-settings/account')}
				>
					<Box sx={styles}>
						<Icon icon="mdi:cog-outline" />
						Settings
					</Box>
				</MenuItem>

				<MenuItem
					sx={{ p: 0 }}
					onClick={() => handleDropdownClose('/pages/pricing')}
				>
					<Box sx={styles}>
						<Icon icon="mdi:currency-usd" />
						Pricing
					</Box>
				</MenuItem>

				<MenuItem
					sx={{ p: 0 }}
					onClick={() => handleDropdownClose('/pages/faq')}
				>
					<Box sx={styles}>
						<Icon icon="mdi:help-circle-outline" />
						FAQ
					</Box>
				</MenuItem> */}

				<Divider />

				<MenuItem
					onClick={handleLogout}
					sx={{
						py: 2,
						'& svg': { mr: 2, fontSize: '1.375rem', color: 'text.primary' }
					}}
				>
					<Icon name="mdi:logout-variant" />
					Logout
				</MenuItem>
			</Menu>
		</React.Fragment>
	);
};

export default UserDropdown;
